import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { authModalReducer, IAuthModalState, initialState } from 'modules/auth';
import { autoTestReducer } from 'modules/auto-test';
import { privateTestReducer } from 'modules/blok-test';
import { courseReducer } from 'modules/courses';
import {
  confirmationModalReducer,
  heartModalReducer,
  limitFinishedModalReducer,
  loadingReducer,
  testFeedbackModalReducer,
  userReducer
} from 'modules/global';
import { learningCenterReducer } from 'modules/learning-centers';
import { lessonReducer } from 'modules/lesson-detail';
import { topicTestReducer } from 'modules/mavzuli-test';
import { nationalCertificateReducer } from 'modules/national-certificate';
import { olympicTestReducer } from 'modules/online-olympic';
import { proExitModalReducer } from 'modules/pro';
import { resultReducer } from 'modules/results';

export const rootReducer = combineReducers({
  authModal: authModalReducer,
  heartModal: heartModalReducer,
  confirmationModal: confirmationModalReducer,
  user: userReducer,
  privateTest: privateTestReducer,
  routeLoading: loadingReducer,
  olympicTest: olympicTestReducer,
  learningCenterModal: learningCenterReducer,
  courses: courseReducer,
  result: resultReducer,
  autoTest: autoTestReducer,
  proExitModal: proExitModalReducer,
  testFeedbackModal: testFeedbackModalReducer,
  nationalCertificate: nationalCertificateReducer,
  limitFinishedModal: limitFinishedModalReducer,
  topicTest: topicTestReducer,
  lesson: lessonReducer,
});

const authModalTransform = createTransform<IAuthModalState, IAuthModalState>(
  inbound => ({
    ...initialState,
    ticketId: inbound.ticketId ?? null,
    redirectUrl: inbound.redirectUrl ?? null
  }),
  outbound => ({
    ...initialState,
    ticketId: outbound.ticketId ?? null,
    redirectUrl: outbound.redirectUrl ?? null
  }),
  { whitelist: ['authModal'] }
);

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['authModal'],
  transforms: [authModalTransform]
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
  devTools: process.env.NODE_ENV !== 'production'
});

export const persistor = persistStore(store);

export type RootStateType = ReturnType<typeof store.getState>;
export type AppDispatchType = typeof store.dispatch;

export const useAppDispatch: () => AppDispatchType = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
